import { useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, Loader2Icon, TrashIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDeleteNote } from '@/api/hooks/useDeleteNote'
import { useNote } from '@/api/hooks/useNote'
import { useNoteGenerationStatus } from '@/api/hooks/useNoteGenerationStatus'
import { useUpdateNote } from '@/api/hooks/useUpdateNote'
import { useUpdateNoteTitle } from '@/api/hooks/useUpdateNoteTitle'
import { useUpdatePendingEvent } from '@/api/hooks/useUpdatePendingEvent'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import { noteDeleteToast } from '@/components/molecules/custom-async-toasts'
import { EmptyState } from '@/components/molecules/empty-state'
import { ConfirmDialog } from '@/components/organisms/confirm-dialog'
import { PendingEventCard } from '@/components/organisms/notes/pending-event-card'
import TimelineVertical, {
  ExtendedTimelineEvent,
} from '@/components/organisms/timeline/timeline-vertical'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'

type Props = {
  /**
   * Set when the note is being read inside an errand (`/errands/$id/notes/$noteId`). Changes only
   * where "back" returns to — same prop and same reasoning as `CreateNotePage`'s `errandId`, so a
   * note opened from an errand's timeline or chat doesn't dump the user out into /notes.
   */
  errandId?: string
  noteId: string
}

export const NoteDetailPage = ({ errandId, noteId }: Props) => {
  const { individualQuery, selectedIndividualId, selectedIndividual } = useIndividuals()
  const { isReadOnly } = useSubscription()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: note, isLoading } = useNote(selectedIndividualId, noteId)

  const goBack = () =>
    errandId
      ? navigate({ params: { id: errandId }, to: '/errands/$id' })
      : navigate({ to: '/notes' })

  const noteIds = useMemo(() => [noteId], [noteId])
  const statuses = useNoteGenerationStatus(selectedIndividualId ?? '', noteIds)
  // Prefer the live RTDB status; fall back to the persisted status on first load.
  const status = statuses[noteId] ?? note?.generationStatus ?? 'idle'
  const isGenerating = status === 'generating'

  const deleteNote = useDeleteNote()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const updateTitle = useUpdateNoteTitle()
  const [titleValue, setTitleValue] = useState('')

  const updateNote = useUpdateNote()
  const [bodyValue, setBodyValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null)

  // The body textarea is always editable (like the create page). Seed it from
  // the loaded note; the body only changes server-side when the user saves, so
  // re-seeding on refetch is a no-op for in-flight edits.
  useEffect(() => {
    if (note) setBodyValue(note.note)
  }, [note?.note])

  // Resize textarea whenever bodyValue changes (covers initial API load, not just user input)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [bodyValue])

  useEffect(() => {
    const el = titleTextareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [titleValue])

  useEffect(() => {
    if (note && !titleValue) setTitleValue(note.title ?? '')
  }, [note?.title])

  const bodyDirty = !!note && bodyValue !== note.note

  const { confirm, dismiss } = useUpdatePendingEvent(selectedIndividualId, noteId)

  // Dated events render in the shared timeline; note events carry no documents.
  // Confirming a pendingEvent re-fetches the note (via cache invalidation), so the
  // newly dated activity appears here without any local optimistic bookkeeping.
  const events: ExtendedTimelineEvent[] = useMemo(
    () =>
      (note?.activities ?? []).map(activity => ({
        date: activity.date,
        day: activity.day,
        description: activity.description ?? '',
        documents: undefined,
        month: activity.month,
        reference: activity.reference,
        type: activity.type ?? 'unknown',
        year: activity.year,
      })),
    [note],
  )

  const pendingEvents = note?.pendingEvents ?? []

  const handleConfirmPendingEvent = (
    eventId: string,
    date: { year: number; month?: number; day?: number },
  ) => {
    if (isReadOnly) return
    void confirm({ eventId, year: date.year, month: date.month, day: date.day })
  }

  const handleDismissPendingEvent = (eventId: string) => {
    if (isReadOnly) return
    void dismiss(eventId)
  }

  const saveTitle = async () => {
    if (isReadOnly) return
    if (!selectedIndividualId || !titleValue.trim() || titleValue.trim() === note?.title) return
    await updateTitle.mutateAsync({
      individualId: selectedIndividualId,
      noteId,
      requestBody: { title: titleValue.trim() },
    })
  }

  const saveBody = async () => {
    if (isReadOnly) return
    if (!selectedIndividualId || bodyValue.trim().length < 3) return
    await updateNote.mutateAsync({
      individualId: selectedIndividualId,
      noteId,
      requestBody: { note: bodyValue },
    })
  }

  return (
    <div className="space-y-8 z-10">
      <div className="flex items-center justify-between">
        <Button onClick={goBack} size="sm" variant="ghost">
          <ArrowLeftIcon />
          {t('global.back')}
        </Button>
        {note && (
          <Button onClick={() => setDeleteOpen(true)} variant="outline">
            <TrashIcon />
            {t('notesTable.rowActionsDropdown.deleteNote')}
          </Button>
        )}
      </div>

      <ConfirmDialog
        onConfirm={() => {
          if (!selectedIndividualId) return
          noteDeleteToast(deleteNote.mutateAsync({ individualId: selectedIndividualId, noteId }))
          goBack()
        }}
        onOpenChange={setDeleteOpen}
        open={deleteOpen}
        variant="destructive"
      />
      <StateSwitcher
        emptyState={<EmptyState body="" heading={t('notesPage.detail.notFound')} />}
        isEmpty={!isLoading && !note}
        isLoading={isLoading || individualQuery.isLoading}
      >
        {note && (
          <div className="space-y-8">
            {/* Note card — title + body as one unified surface */}
            <div className="rounded-lg border bg-layer-1 overflow-hidden">
              <div className="px-4 pt-4 pb-2">
                {isGenerating && !note.title ? (
                  <div className="flex items-center gap-1.5 text-text-secondary text-sm h-9">
                    <Loader2Icon className="size-3.5 animate-spin" />
                    {t('notesPage.detail.generatingTitle')}
                  </div>
                ) : (
                  <Textarea
                    className="border-0 bg-transparent shadow-none font-semibold text-lg px-0 focus-visible:ring-0 resize-none overflow-hidden leading-snug min-h-0"
                    onBlur={saveTitle}
                    onChange={e => setTitleValue(e.target.value)}
                    placeholder={t('notesTable.untitled')}
                    readOnly={isReadOnly}
                    ref={titleTextareaRef}
                    rows={1}
                    value={titleValue}
                  />
                )}
              </div>
              <Textarea
                className="border-0 rounded-none bg-transparent shadow-none resize-none overflow-hidden min-h-[120px] focus-visible:ring-0 px-4 py-3"
                onChange={e => setBodyValue(e.target.value)}
                readOnly={isReadOnly}
                ref={textareaRef}
                value={bodyValue}
              />
              <div className="flex justify-end px-4 pb-3">
                <Button
                  disabled={
                    isReadOnly || !bodyDirty || bodyValue.trim().length < 3 || updateNote.isPending
                  }
                  onClick={saveBody}
                >
                  {t('global.save')}
                </Button>
              </div>
            </div>

            {/* Timeline (dated events) */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h5 className="text-base font-bold">{t('notesPage.detail.timelineSection')}</h5>
                {isGenerating && (
                  <Loader2Icon className="size-4 animate-spin text-text-secondary" />
                )}
              </div>
              {events.length > 0 ? (
                <div className="bg-layer-1 p-4 rounded-md border">
                  <TimelineVertical compact events={events} />
                </div>
              ) : (
                !isGenerating && (
                  <p className="text-text-secondary">{t('notesPage.detail.noEvents')}</p>
                )
              )}
            </div>

            {/* Time-ambiguous events the AI could not date */}
            {pendingEvents.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-base font-bold">{t('notesPage.detail.needsDateSection')}</h5>
                <div className="flex flex-col gap-3">
                  {pendingEvents.map((event, index) => (
                    <PendingEventCard
                      birthDate={selectedIndividual?.birthDate ? new Date(selectedIndividual.birthDate) : undefined}
                      event={event}
                      isDisabled={isReadOnly}
                      key={event.id ?? index}
                      onConfirm={handleConfirmPendingEvent}
                      onDismiss={handleDismissPendingEvent}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </StateSwitcher>
    </div>
  )
}
